// src/services/StampImageService.js - Debug version combining working elements
import { toPng, toBlob } from 'html-to-image';
import React from 'react';
import ReactDOM from 'react-dom/client';
import Stamp, { getStampDimensions } from '../components/stamp/Stamp';

/**
 * Service for converting React Stamp components to images
 * DEBUG VERSION - Restored working functionality
 */
class StampImageService {
  // Cache for generated stamp images
  static imageCache = new Map();
  
  // Temporary container for rendering stamps
  static renderContainer = null;

  // Debug flag - ENABLED for troubleshooting
  static debugMode = false;

  /**
   * Initialize the service (creates hidden render container)
   */
  static initialize() {
    if (StampImageService.renderContainer) return;
    
    // Create hidden container for rendering stamps with better styling
    StampImageService.renderContainer = document.createElement('div');
    StampImageService.renderContainer.id = 'stamp-image-service-container';
    StampImageService.renderContainer.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: auto;
      height: auto;
      pointer-events: none;
      z-index: -1;
      font-family: Helvetica, Arial, sans-serif;
      background: white;
      font-smooth: always;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    `;
    document.body.appendChild(StampImageService.renderContainer);
  }

  /**
   * Generate cache key for a stamp configuration
   */
  static getCacheKey(config, documentIndex, caseData, scale = 1) {
    const caseNumber = caseData?.caseNumber || caseData?.name || 'unknown';
    return `${JSON.stringify(config)}_${documentIndex}_${caseNumber}_${scale}`;
  }

  /**
   * Test if a data URL can actually be loaded as an image
   */
  static async testDataUrlAsImage(dataUrl) {
    return new Promise((resolve) => {
      const testImg = new Image();
      
      testImg.onload = () => {
        /*
        console.log('✅ Data URL loads as valid image:', {
          naturalWidth: testImg.naturalWidth,
          naturalHeight: testImg.naturalHeight,
          complete: testImg.complete
        });
        */
        resolve({
          success: true,
          width: testImg.naturalWidth,
          height: testImg.naturalHeight,
          complete: testImg.complete
        });
      };
      
      testImg.onerror = (error) => {
        console.error('❌ Data URL failed to load as image:', error);
        resolve({
          success: false,
          error: error
        });
      };
      
      // Set a timeout
      setTimeout(() => {
        if (!testImg.complete) {
          console.warn('⏰ Data URL image load timed out');
          resolve({
            success: false,
            error: 'Timeout'
          });
        }
      }, 3000);
      
      testImg.src = dataUrl;
    });
  }

  /**
   * Convert React Stamp component to data URL - RESTORED WORKING VERSION
   */
  static async generateStampDataUrl(config, documentIndex, caseData, scale = 2, waitTime = 200) {
    const cacheKey = StampImageService.getCacheKey(config, documentIndex, caseData, scale);
    
    // Return cached version if available
    if (StampImageService.imageCache.has(cacheKey)) {
      return StampImageService.imageCache.get(cacheKey);
    }
    
    try {
      StampImageService.initialize();
      
      // Create temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = `
        display: inline-block;
        background: white;
        padding: 0;
        font-family: Helvetica, Arial, sans-serif;
        font-weight: bold;
        line-height: 1.1;
        box-sizing: border-box;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
        image-rendering: crisp-edges;
        -webkit-image-rendering: crisp-edges;
        transform: translateZ(0);
        will-change: transform;
      `;
      StampImageService.renderContainer.appendChild(tempContainer);

      // Create React root and render stamp
      const root = ReactDOM.createRoot(tempContainer);
      
      // Render the stamp component
      await new Promise((resolve, reject) => {
        try {
          root.render(React.createElement(Stamp, {
            config,
            documentIndex,
            caseData,
            scale,
            style: {
              fontFamily: 'Helvetica, Arial, sans-serif',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              imageRendering: 'crisp-edges',
              WebkitImageRendering: 'crisp-edges',
              transform: 'translateZ(0)',
            }
          }));
          
          // Use working wait time
          setTimeout(resolve, 400);
        } catch (error) {
          console.error('❌ Error rendering React component:', error);
          reject(error);
        }
      });

      // Check what was actually rendered
      const rect = tempContainer.getBoundingClientRect();
      /*
      console.log('📏 Container dimensions after render:', {
        width: rect.width,
        height: rect.height,
        hasContent: tempContainer.children.length > 0,
        childElements: tempContainer.children.length,
        innerHTML: tempContainer.innerHTML.substring(0, 200) + '...'
      });
      */

      // Convert to image - RESTORED WORKING SETTINGS
      const dataUrl = await toPng(tempContainer, {
        quality: 1.0,
        pixelRatio: 3, // RESTORED: Working version used pixelRatio 3
        backgroundColor: 'transparent',
        width: Math.ceil(rect.width) || 432,
        height: Math.ceil(rect.height) || 243,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          fontFamily: 'Helvetica, Arial, sans-serif',
        },
        filter: (node) => {
          return true;
        },
        skipAutoScale: false,
        cacheBust: true
      });

      // Enhanced analysis of generated image
      /*
      console.log('🔍 Generated image analysis:', {
        dataUrlLength: dataUrl.length,
        startsWithDataImage: dataUrl.startsWith('data:image/'),
        sizeBytes: Math.round((dataUrl.length * 3) / 4),
        dataUrlStart: dataUrl.substring(0, 100),
        isPNG: dataUrl.startsWith('data:image/png'),
        isJPEG: dataUrl.startsWith('data:image/jpeg')
      });
      */
      // Cleanup
      root.unmount();
      StampImageService.renderContainer.removeChild(tempContainer);

      // Validate the result
      if (!dataUrl || dataUrl.length < 200) {
        throw new Error(`Generated image appears to be invalid (length: ${dataUrl ? dataUrl.length : 0})`);
      }

      // Cache the result
      StampImageService.imageCache.set(cacheKey, dataUrl);
      
      return dataUrl;

    } catch (error) {
      console.error('❌ Error generating stamp image:', error);
      
      // RESTORED: Detailed error information
      const detailedError = new Error(`Stamp image generation failed: ${error.message}`);
      detailedError.originalError = error;
      detailedError.config = config;
      detailedError.documentIndex = documentIndex;
      detailedError.caseData = caseData;
      detailedError.scale = scale;
      
      throw detailedError;
    }
  }

/**
   * Convert React Stamp component to blob for PDF embedding
   * Uses 200ms timeout by default, retries with 500ms if needed
   */
  static async generateStampBlob(config, documentIndex, caseData, scale = 2, waitTime = 200) {
    try {
      const dataUrl = await StampImageService.generateStampDataUrl(config, documentIndex, caseData, scale, waitTime);
      
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      return uint8Array;
      
    } catch (error) {
      // Retry once with longer timeout if this was the fast attempt
      if (waitTime === 200) {
        console.warn(`⚠️ Stamp generation failed at 200ms, retrying at 500ms...`);
        return StampImageService.generateStampBlob(config, documentIndex, caseData, scale, 500);
      }
      
      // If 500ms failed, something is really wrong
      console.error('❌ Error generating stamp blob after 500ms retry:', error);
      throw error;
    }
  }

  /**
   * Clear cache for stamps matching a configuration
   */
  static clearCache(config = null) {
    if (!config) {
      console.log('📸 Clearing all stamp cache');
      StampImageService.imageCache.clear();
    } else {
      const configStr = JSON.stringify(config);
      const keysToDelete = [];
      
      for (const [key] of StampImageService.imageCache) {
        if (key.startsWith(configStr)) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => StampImageService.imageCache.delete(key));
      console.log('📸 Cleared stamp cache for config, removed:', keysToDelete.length);
    }
  }

  /**
   * Get the physical dimensions of a stamp at given scale
   */
  static getStampDimensions(scale = 1) {
    return getStampDimensions(scale); // Use the imported function
  }

  /**
   * Cleanup method - call when app is shutting down
   */
  static cleanup() {
    console.log('🧹 Cleaning up StampImageService...');
    
    if (StampImageService.renderContainer) {
      document.body.removeChild(StampImageService.renderContainer);
      StampImageService.renderContainer = null;
    }
    StampImageService.imageCache.clear();
    
    console.log('✅ StampImageService cleanup complete');
  }

  /**
   * Get debug information about the service
   */
  static getDebugInfo() {
    return {
      containerExists: !!StampImageService.renderContainer,
      cacheSize: StampImageService.imageCache.size,
      cacheKeys: Array.from(StampImageService.imageCache.keys()),
      containerInDOM: StampImageService.renderContainer ? 
        document.body.contains(StampImageService.renderContainer) : false
    };
  }
}

export default StampImageService;